import React from 'react';
import { Alert, Button, Empty, Spin, Tag, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const { Title, Text } = Typography;

/*
 * Shared furniture for the entity detail pages global search routes to.
 *
 * The pieces live together because the value of these pages is that an asset,
 * a liability and a participant all read the same way: a header that names the
 * thing, the few numbers worth leading with, then sections of fields. Five
 * pages each inventing that structure is how they end up looking unrelated.
 */

const PAGE_BG_CARD = '#ffffff';
const BORDER = '#e0e0e0';
const MUTED = '#888888';
const ACCENT = '#3b82f6';

const Page = styled.div`
  max-width: 960px;
  margin: auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Card = styled.section`
  background: ${PAGE_BG_CARD};
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const HeaderCard = styled(Card)`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
`;

const HeaderMain = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  min-width: 260px;
  flex: 1;
`;

/* A tinted square rather than a bare icon: at 48px it gives the header a
   left edge to hang from, which a loose glyph does not. */
const IconChip = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  border-radius: 12px;
  background: rgba(59, 130, 246, 0.1);
  color: ${ACCENT};
  font-size: 1.25rem;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
`;

const EyebrowRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const Eyebrow = styled.span`
  font-size: 0.7rem;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const StatRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
`;

const StatCard = styled(Card)`
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const StatLabel = styled.span`
  font-size: 0.75rem;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

/* Tabular figures so a column of amounts lines up on the decimal. Breaks at
   word boundaries only, so a long value wraps rather than splitting a word. */
const StatValue = styled.span<{ $muted?: boolean }>`
  font-size: 1.35rem;
  font-weight: 600;
  color: ${(props) => (props.$muted ? MUTED : '#333')};
  font-variant-numeric: tabular-nums;
  overflow-wrap: break-word;
  line-height: 1.3;
`;

const SectionTitle = styled.h3`
  margin: 0 0 1rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: #333;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid ${BORDER};
`;

const FieldGrid = styled.dl`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1rem 2rem;
  margin: 0;
`;

const FieldItem = styled.div<{ $span?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
  ${(props) => props.$span && 'grid-column: 1 / -1;'}
`;

const FieldLabel = styled.dt`
  font-size: 0.75rem;
  color: ${MUTED};
`;

const FieldValue = styled.dd<{ $unset?: boolean }>`
  margin: 0;
  font-size: 0.9rem;
  color: ${(props) => (props.$unset ? '#bbb' : '#333')};
  font-style: ${(props) => (props.$unset ? 'italic' : 'normal')};
  overflow-wrap: anywhere;
  white-space: pre-line;
`;

const RelatedList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

const RelatedRow = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const RelatedName = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.15rem;
  min-width: 0;
`;

/* A plain button rather than antd's link button: that one carries its own
   horizontal padding, which pushed the name out of line with the meta text
   directly beneath it. */
const RelatedLink = styled.button`
  padding: 0;
  border: none;
  background: none;
  color: ${ACCENT};
  font-size: 0.9rem;
  font-weight: 500;
  text-align: left;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const RelatedMeta = styled.span`
  font-size: 0.75rem;
  color: ${MUTED};
`;

const RelatedAmount = styled.span`
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

/* ---------- formatting helpers, shared so every page agrees ---------- */

/* Pinned to en-US: the estates are US probate, and leaving the locale to the
   browser renders the same figure as "US$870,016.00" outside the US. */
export const formatCurrency = (value?: string | number | null): string | null => {
  const amount = typeof value === 'string' ? Number(value) : value;
  if (typeof amount !== 'number' || Number.isNaN(amount)) return null;
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  });
};

export const formatBool = (value?: boolean | null): string | null =>
  typeof value === 'boolean' ? (value ? 'Yes' : 'No') : null;

/** Joins the parts of an address that are actually filled in. */
export const formatAddress = (address?: {
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  postalCode?: string | null;
  county?: string | null;
  country?: string | null;
} | null): string | null => {
  if (!address) return null;

  const street = [address.addressLine1, address.addressLine2].filter(Boolean).join(', ');
  const region = [address.city, address.state, address.zipCode || address.postalCode]
    .filter(Boolean)
    .join(' ');
  const tail = [address.county ? `${address.county} County` : '', address.country]
    .filter(Boolean)
    .join(', ');

  const full = [street, region, tail].filter(Boolean).join('\n');
  return full || null;
};

/* ---------- components ---------- */

interface FieldProps {
  label: string;
  value?: React.ReactNode;
  /** Take the full grid width, for prose and addresses. */
  span?: boolean;
  /** Shown in place of an empty value. */
  emptyText?: string;
}

/**
 * A labelled value. Empty renders as a muted placeholder rather than a blank
 * gap, so a sparse record reads as "not captured yet" instead of broken.
 */
export const Field: React.FC<FieldProps> = ({ label, value, span, emptyText = 'Not set' }) => {
  const isEmpty =
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0);

  return (
    <FieldItem $span={span}>
      <FieldLabel>{label}</FieldLabel>
      <FieldValue $unset={isEmpty}>{isEmpty ? emptyText : value}</FieldValue>
    </FieldItem>
  );
};

interface SectionProps {
  title: string;
  children: React.ReactNode;
  /** Renders an empty state instead of the children. */
  empty?: boolean;
  emptyText?: string;
}

export const DetailSection: React.FC<SectionProps> = ({ title, children, empty, emptyText }) => (
  <Card>
    <SectionTitle>{title}</SectionTitle>
    {empty ? (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText || 'Nothing recorded'} />
    ) : (
      children
    )}
  </Card>
);

export const DetailFields: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <FieldGrid>{children}</FieldGrid>
);

export interface Stat {
  label: string;
  value?: string | null;
  emptyText?: string;
}

/** The two to four numbers worth reading before anything else. */
export const DetailStats: React.FC<{ stats: Stat[] }> = ({ stats }) => (
  <StatRow>
    {stats.map((stat) => (
      <StatCard key={stat.label}>
        <StatLabel>{stat.label}</StatLabel>
        <StatValue $muted={!stat.value}>{stat.value || stat.emptyText || 'Not set'}</StatValue>
      </StatCard>
    ))}
  </StatRow>
);

export interface RelatedItem {
  key: string;
  name: string;
  meta?: string;
  amount?: string | null;
  onClick?: () => void;
}

/** Records hanging off this one: an asset's liabilities, a person's estates. */
export const DetailRelated: React.FC<{ items: RelatedItem[] }> = ({ items }) => (
  <RelatedList>
    {items.map((item) => (
      <RelatedRow key={item.key}>
        <RelatedName>
          {item.onClick ? (
            <RelatedLink type="button" onClick={item.onClick}>
              {item.name}
            </RelatedLink>
          ) : (
            <Text strong>{item.name}</Text>
          )}
          {item.meta && <RelatedMeta>{item.meta}</RelatedMeta>}
        </RelatedName>
        {item.amount && <RelatedAmount>{item.amount}</RelatedAmount>}
      </RelatedRow>
    ))}
  </RelatedList>
);

interface DetailPageProps {
  /** Entity type, shown above the title. */
  eyebrow: string;
  title: string;
  icon?: React.ReactNode;
  tags?: { label: string; color?: string }[];
  loading?: boolean;
  error?: string | null;
  backTo: string;
  backLabel: string;
  actions?: React.ReactNode;
  children?: React.ReactNode;
}

const DetailPage: React.FC<DetailPageProps> = ({
  eyebrow,
  title,
  icon,
  tags,
  loading,
  error,
  backTo,
  backLabel,
  actions,
  children,
}) => {
  const navigate = useNavigate();

  const backButton = (
    <Button
      type="link"
      icon={<ArrowLeftOutlined />}
      style={{ paddingLeft: 0, alignSelf: 'flex-start' }}
      onClick={() => navigate(backTo)}
    >
      {backLabel}
    </Button>
  );

  if (loading) {
    return (
      <Page>
        <Card style={{ textAlign: 'center', padding: '3rem' }}>
          <Spin tip="Loading..." />
        </Card>
      </Page>
    );
  }

  if (error) {
    return (
      <Page>
        {backButton}
        <Alert message="Could not load this record" description={error} type="error" showIcon />
      </Page>
    );
  }

  return (
    <Page>
      {backButton}
      <HeaderCard>
        <HeaderMain>
          {icon && <IconChip>{icon}</IconChip>}
          <HeaderText>
            <EyebrowRow>
              <Eyebrow>{eyebrow}</Eyebrow>
              {tags?.map((tag) => (
                <Tag key={tag.label} color={tag.color} style={{ marginInlineEnd: 0 }}>
                  {tag.label}
                </Tag>
              ))}
            </EyebrowRow>
            <Title level={3} style={{ margin: 0 }}>
              {title}
            </Title>
          </HeaderText>
        </HeaderMain>
        {actions && <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>{actions}</div>}
      </HeaderCard>
      {children}
    </Page>
  );
};

export default DetailPage;
